/**
 * UI State Store
 * Manages global UI state including sidebar, modals, and notifications
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

/**
 * Notification type
 */
export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  description?: string;
  read: boolean;
  duration?: number; // milliseconds, undefined = no auto-dismiss
  timestamp: number;
}

/**
 * Modal state
 */
export interface ModalState {
  isOpen: boolean;
  title?: string;
  content?: React.ReactNode;
  onConfirm?: () => void;
  onCancel?: () => void;
}

/**
 * UI State Interface
 */
interface UIState {
  // Sidebar state
  sidebarCollapsed: boolean;

  // Modal state
  modal: ModalState;

  // Notifications
  notifications: Notification[];

  // Loading state (for global loading indicators)
  isLoading: boolean;
  loadingMessage?: string;

  // Actions
  toggleSidebarCollapsed: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  openModal: (modal: Omit<ModalState, 'isOpen'>) => void;
  closeModal: () => void;

  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  removeNotification: (id: string) => void;
  markNotificationRead: (id: string) => void;
  clearNotifications: () => void;

  setLoading: (loading: boolean, message?: string) => void;
}

/**
 * Create the UI store with persistence for user preferences
 */
export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial state
      sidebarCollapsed: false,
      modal: { isOpen: false },
      notifications: [],
      isLoading: false,
      loadingMessage: undefined,

      // Sidebar actions
      toggleSidebarCollapsed: () =>
        set((state) => ({
          sidebarCollapsed: !state.sidebarCollapsed,
        })),

      setSidebarCollapsed: (collapsed: boolean) =>
        set({
          sidebarCollapsed: collapsed,
        }),

      // Modal actions
      openModal: (modal) =>
        set({
          modal: {
            ...modal,
            isOpen: true,
          },
        }),

      closeModal: () =>
        set({
          modal: { isOpen: false },
        }),

      // Notification actions
      addNotification: (notification) => {
        const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newNotification: Notification = {
          ...notification,
          id,
          read: false,
          timestamp: Date.now(),
          duration: notification.duration, // No auto-dismiss by default
        };

        set((state) => ({
          notifications: [...state.notifications, newNotification],
        }));

        // Auto-dismiss if duration is set
        if (newNotification.duration && newNotification.duration > 0) {
          setTimeout(() => {
            get().removeNotification(id);
          }, newNotification.duration);
        }

        return id;
      },

      removeNotification: (id: string) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        })),

      markNotificationRead: (id: string) =>
        set((state) => ({
          notifications: state.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
        })),

      clearNotifications: () =>
        set({
          notifications: [],
        }),

      // Loading actions
      setLoading: (loading: boolean, message?: string) =>
        set({
          isLoading: loading,
          loadingMessage: message,
        }),
    }),
    {
      name: 'skillspec-ui-store', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist user preferences, not transient state
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        // Don't persist: modal, notifications, loading
      }),
    }
  )
);

/**
 * Selector hooks for optimized re-renders
 */

// Sidebar selectors
export const useSidebarCollapsed = () => useUIStore((state) => state.sidebarCollapsed);
export const useSidebarActions = () =>
  useUIStore(
    useShallow((state) => ({
      toggleSidebarCollapsed: state.toggleSidebarCollapsed,
      setSidebarCollapsed: state.setSidebarCollapsed,
    }))
  );

// Modal selectors
export const useModal = () => useUIStore((state) => state.modal);
export const useModalActions = () =>
  useUIStore(
    useShallow((state) => ({
      openModal: state.openModal,
      closeModal: state.closeModal,
    }))
  );

// Notification selectors
export const useNotifications = () => useUIStore((state) => state.notifications);
export const useNotificationActions = () =>
  useUIStore(
    useShallow((state) => ({
      addNotification: state.addNotification,
      removeNotification: state.removeNotification,
      markNotificationRead: state.markNotificationRead,
      clearNotifications: state.clearNotifications,
    }))
  );

// Loading selectors
export const useLoading = () =>
  useUIStore(
    useShallow((state) => ({
      isLoading: state.isLoading,
      loadingMessage: state.loadingMessage,
    }))
  );
export const useLoadingActions = () => useUIStore((state) => state.setLoading);
